import {
  AfterViewInit, ChangeDetectorRef, Directive, ElementRef, Input, NgZone, OnChanges, OnDestroy, OnInit, Renderer2,
  TemplateRef
} from '@angular/core';
import { PinnedHeaderMirror } from '../utils/pinned-header-mirror';

/**
 * Pins the title band and column header row of the table being scrolled through under the app
 * header on phones (RA-2838), for pages that stack several `.sticky-table` cards.
 *
 * Each table's `.sticky-table` is its own `overflow: auto` box, so CSS `position: sticky` can't
 * pin anything in it against the page. Instead the page renders one fixed host carrying this
 * directive, and each card registers itself with `rmsPinnedTableSource`. The page renders the
 * active card's band and header row into the host from the SAME `ng-template`s the card renders,
 * so the pinned copy is live: taps sort, carets and counts update. A `cloneNode` copy loses its
 * handlers and goes stale.
 *
 * This directive only does geometry:
 * - which card is active: the last one whose top has reached the pin line and whose table hasn't
 *   scrolled past it. At that moment the copy lands exactly over the real band, so the first pin
 *   and every handoff are seamless.
 * - the push-up offset: as the active table's bottom rises, the stack slides up with it.
 * - column widths and sideways position, through `PinnedHeaderMirror`: the copy's header row
 *   sits in its own scrolling viewport kept in step with its table, so a sticky SYM column keeps
 *   working in both.
 *
 * The pin line is read off the host's own box (its `top` is the stylesheet's), so the app
 * header's height isn't mirrored as a constant here. Scrolling runs outside the Angular zone;
 * change detection only runs when the active card changes.
 */
@Directive({
  selector: '[rmsPinnedTableStack]',
  exportAs: 'rmsPinnedTableStack',
  standalone: false
})
export class PinnedTableStackDirective implements AfterViewInit, OnDestroy {
  /** Phones only. Matches the tables' own `media-breakpoint-down(md)`; at 768 Movers goes two-up. */
  public static readonly PHONE_QUERY = '(max-width: 767.98px)';

  /** The card whose band and header are pinned, or null when nothing is. Read by the page template. */
  public active: PinnedTableSourceDirective | null = null;

  private readonly sources = new Set<PinnedTableSourceDirective>();
  private mediaQuery: MediaQueryList | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private mirror: PinnedHeaderMirror | null = null;
  private readonly pageListeners: Array<() => void> = [];
  private frame: number | null = null;
  private needsLayout = true;
  /** The translateY currently applied, so the pin line can be read back off the moved host. */
  private offset = 0;
  private viewInit = false;

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    private readonly ngZone: NgZone,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) { }

  ngAfterViewInit(): void {
    this.viewInit = true;
    this.mediaQuery = window.matchMedia?.(PinnedTableStackDirective.PHONE_QUERY) ?? null;
    const host = this.elementRef.nativeElement;

    this.ngZone.runOutsideAngular(() => {
      const onScroll = () => this.scheduleUpdate(false);
      const onResize = () => this.scheduleUpdate();
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onResize);
      this.mediaQuery?.addEventListener?.('change', onResize);
      this.pageListeners.push(
        () => window.removeEventListener('scroll', onScroll),
        () => window.removeEventListener('resize', onResize),
        () => this.mediaQuery?.removeEventListener?.('change', onResize)
      );

      // The page moves without a scroll event when a card above grows or shrinks (Stock Halts,
      // a refresh, an expanded row, an appended page of rows). The cards and the page container
      // cover those; the host covers the stack's own height changing, e.g. a sort line appearing.
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.scheduleUpdate());
        this.resizeObserver.observe(host);
        if (host.parentElement) {
          this.resizeObserver.observe(host.parentElement);
        }
        this.sources.forEach(source => this.resizeObserver.observe(source.element));
      }
    });

    this.scheduleUpdate();
  }

  ngOnDestroy(): void {
    this.pageListeners.forEach(remove => remove());
    this.unbindMirror();
    this.resizeObserver?.disconnect();
    if (this.frame !== null) {
      window.cancelAnimationFrame(this.frame);
    }
  }

  /** Adds a card to the set that can pin. Called by `rmsPinnedTableSource`. */
  public register(source: PinnedTableSourceDirective): void {
    this.sources.add(source);
    this.resizeObserver?.observe(source.element);
    this.scheduleUpdate();
  }

  /**
   * Removes a card, e.g. one taken out by `*ngIf`. This runs during change detection, so a pin it
   * held is dropped on the next frame rather than re-rendered from here.
   */
  public unregister(source: PinnedTableSourceDirective): void {
    this.sources.delete(source);
    this.resizeObserver?.unobserve(source.element);
    this.scheduleUpdate();
  }

  /**
   * Queues a recompute for the next frame, coalescing every trigger in between.
   * @param layout: also re-measure column widths and the host's box. A plain vertical scroll
   *   only needs the active card and the offset.
   */
  public scheduleUpdate(layout = true): void {
    this.needsLayout = this.needsLayout || layout;
    if (!this.viewInit || this.frame !== null) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      this.frame = window.requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    });
  }

  /**
   * Scrolls the page so the active card's top meets the pin line, which puts the copy exactly over
   * the real band and header and leaves the table's first row just under it. For a sort or Reset
   * tapped in the copy: the reader is deep in a table whose rows have just reordered. Waits a
   * frame so the re-sorted rows and any change to the pinned line have rendered.
   */
  public scrollActiveToFirstRow(): void {
    const source = this.active;
    if (!source) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      window.requestAnimationFrame(() => {
        // Rounded toward zero so the card's top stays on or above the pin line and it stays pinned.
        const delta = Math.trunc(source.element.getBoundingClientRect().top - this.pinLine());
        if (delta !== 0) {
          window.scrollBy(0, delta);
        }
      });
    });
  }

  /** Picks the active card, then places and sizes the stack over it. */
  private update(): void {
    const next = this.mediaQuery?.matches ? this.findActive() : null;
    if (next !== this.active) {
      this.setActive(next);
    }
    if (!this.active) {
      this.clearPosition();
      return;
    }
    if (this.needsLayout) {
      this.needsLayout = false;
      this.layout(this.active);
    }
    this.position(this.active);
  }

  /** The last card whose top has reached the pin line and whose table still reaches below it. */
  private findActive(): PinnedTableSourceDirective | null {
    const pinTop = this.pinLine();
    let found: PinnedTableSourceDirective | null = null;
    this.sources.forEach(source => {
      const table = source.scrollElement;
      if (!source.pinEnabled || !table) {
        return;
      }
      if (source.element.getBoundingClientRect().top <= pinTop && table.getBoundingClientRect().bottom > pinTop) {
        found = source;
      }
    });
    return found;
  }

  /** The y the stack pins at: the host's own top, less whatever push-up is applied to it. */
  private pinLine(): number {
    return this.elementRef.nativeElement.getBoundingClientRect().top - this.offset;
  }

  /**
   * Swaps the rendered copy. Inside the zone, so the copy's own listeners (sort taps, Reset)
   * are bound where they trigger change detection; rendered synchronously so the copy can be
   * measured and placed in this same frame, before it paints.
   */
  private setActive(source: PinnedTableSourceDirective | null): void {
    this.unbindMirror();
    this.ngZone.run(() => {
      this.active = source;
      this.changeDetectorRef.detectChanges();
    });
    this.needsLayout = true;
    const table = source?.scrollElement;
    const viewport = this.elementRef.nativeElement.querySelector('.pinned-table-viewport') as HTMLElement | null;
    if (table && viewport) {
      this.mirror = new PinnedHeaderMirror(table, viewport, this.ngZone, () => this.scheduleUpdate());
    }
  }

  /** Sizes the host to the active table's box, then mirrors its columns and sideways offset. */
  private layout(source: PinnedTableSourceDirective): void {
    const box = source.scrollElement?.getBoundingClientRect();
    if (!box || !this.mirror) {
      return;
    }
    // Fractional: offsetWidth rounds, and the copy has to sit exactly over the table.
    const host = this.elementRef.nativeElement;
    this.renderer.setStyle(host, 'left', `${box.left}px`);
    this.renderer.setStyle(host, 'width', `${box.width}px`);
    this.mirror.layout();
  }

  /** Applies the push-up: once the table's bottom rises into the stack, the stack goes with it. */
  private position(source: PinnedTableSourceDirective): void {
    const host = this.elementRef.nativeElement;
    const pinTop = this.pinLine();
    const height = host.getBoundingClientRect().height;
    const bottom = source.scrollElement?.getBoundingClientRect().bottom ?? pinTop;
    const offset = Math.min(0, bottom - pinTop - height);
    if (offset !== this.offset) {
      this.offset = offset;
      this.renderer.setStyle(host, 'transform', offset ? `translateY(${offset}px)` : null);
    }
  }

  private clearPosition(): void {
    const host = this.elementRef.nativeElement;
    this.offset = 0;
    ['transform', 'left', 'width'].forEach(property => this.renderer.removeStyle(host, property));
  }

  private unbindMirror(): void {
    this.mirror?.destroy();
    this.mirror = null;
  }
}

/**
 * Marks a table card (its title band followed by its `.sticky-table`) as one that can pin in a
 * page's `rmsPinnedTableStack`, and hands the stack the templates to render the copy from. The
 * card must render the same templates itself, so the copy and the real table can't drift apart.
 */
@Directive({
  selector: '[rmsPinnedTableSource]',
  standalone: false
})
export class PinnedTableSourceDirective implements OnInit, OnChanges, OnDestroy {
  /** The page's stack this card pins in. */
  @Input('rmsPinnedTableSource') stack: PinnedTableStackDirective | null = null;
  /** The title band above the table. */
  @Input() pinBand: TemplateRef<unknown> | null = null;
  /** The header `<tr>`. */
  @Input() pinHead: TemplateRef<unknown> | null = null;
  /** An optional full-width line pinned under the header row, e.g. Movers' "Sorted by". */
  @Input() pinLine: TemplateRef<unknown> | null = null;
  @Input() pinLineVisible = false;
  /** Context the templates are rendered with. */
  @Input() pinContext: unknown = null;
  /** False for a table that must never pin, e.g. one showing its no-content row. */
  @Input() pinEnabled = true;
  /** The card's sideways-scrolling table box, for cards whose table doesn't sit in a `.sticky-table`. */
  @Input() pinScrollSelector = '.sticky-table';

  /**
   * `pinContext` plus `pinned: true`, for the copy. Templates use it to take the copy's controls
   * out of the tab order, since the copy is hidden from assistive tech, and to tell their handlers
   * the tap came from the copy.
   */
  public copyContext: Record<string, unknown> = { pinned: true };

  constructor(private readonly elementRef: ElementRef<HTMLElement>) { }

  ngOnInit(): void {
    this.stack?.register(this);
  }

  ngOnChanges(): void {
    // Spreading a null context is a no-op, so no fallback is needed.
    this.copyContext = { ...(this.pinContext as Record<string, unknown>), pinned: true };
    this.stack?.scheduleUpdate();
  }

  ngOnDestroy(): void {
    this.stack?.unregister(this);
  }

  public get element(): HTMLElement {
    return this.elementRef.nativeElement;
  }

  /** The card's horizontally scrolling table box, looked up live since it can sit under `*ngIf`. */
  public get scrollElement(): HTMLElement | null {
    return this.elementRef.nativeElement.querySelector(this.pinScrollSelector);
  }
}
